import MenuList from "@mui/material/MenuList";
import ListItemText from "@mui/material/ListItemText";
import Badge from "@mui/material/Badge";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import * as React from "react";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { alpha } from "@mui/material/styles";
import { common } from "@mui/material/colors";
import {ActualDateTime, updateData} from "../../utils/utils";
import {useNotificationList, useNotificationStore} from "../../store/notificationStore";
import Divider from "@mui/material/Divider";
import {useNavigate} from "react-router-dom";
import {Notification} from "../../models/Notification";
import {useTranslation} from "react-i18next";
import WarningIcon from "@mui/icons-material/Warning";
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import GppBadIcon from '@mui/icons-material/GppBad';
import InfoIcon from '@mui/icons-material/Info';
import {NotificationType} from "../../models/NotificationType";
import BasicPopper from "../Common/BasicPopper";
import {API_URL} from "../../utils/config";

export function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case NotificationType.ALERT:
      return <AssignmentTurnedInIcon color={"success"}/>
    case NotificationType.ERROR:
      return <GppBadIcon color={"error"}/>
    case NotificationType.WARNING:
      return <WarningIcon color={"warning"}/>
    default:
      return <InfoIcon color={"info"}/>
  }
}

const NotificationIcon = () => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const {t} = useTranslation();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [notifications, unreadCount] = useNotificationList();
  const { getUnreadCount, getAll } = useNotificationStore();

  const handleAll = () => {
    handleClose()
    navigate("/store/notifications")
  }

  const handleMarkAllAsRead = async () => {
    try {
      await updateData(`${API_URL}/notifications/read`, {});
      getAll()
      getUnreadCount()
    } catch (ex: any) {
      console.log(ex)
    }
  }

  const handleNotificationClick = (notification: Notification) => {
    handleClose()
    navigate(`/store/notifications/${notification.uuid}`)
  }

  return (
    <Stack flexDirection={"column"} alignItems={"flex-end"}>
      <IconButton
        sx={{
          backgroundColor: "none",
          "&:hover": {
            backgroundColor: alpha(common.white, 0.25),
          },
          borderRadius: "8px",
        }}
        id="notificationsPopper"
        aria-controls={open ? "notificationsPopper-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsNoneOutlinedIcon
            sx={{ color: "common.white", width: 24 }}
          />
        </Badge>
      </IconButton>
      <BasicPopper
        id={"notificationsPopper-menu"}
        anchorEl={anchorEl}
        open={open}
        handleClose={handleClose}
        title={t("toolbar.notifications")}
      >
        <MenuList>
          {notifications.map((item, index) => (
            <MenuItem onClick={() => handleNotificationClick(item)} key={index} selected={!item.readAt}>
              <Stack
                flexDirection={"row"}
                columnGap={2}
                alignItems={"center"}
                flexGrow={1}
                style={{ fontWeight: item.readAt ? "normal" : "bold" }}
              >
                {getNotificationIcon(item.notificationType)}
                <Stack flexDirection={"column"} rowGap={0.5}>
                  <Typography noWrap variant={"body1"} style={{ fontWeight: "inherit" }}>
                    {item.label}
                  </Typography>
                  <Typography variant={"body2"} color={"primary"} style={{ fontWeight: "inherit" }}>
                    {item.notificationType}
                  </Typography>
                </Stack>
                <div style={{ flexGrow: 1 }}></div>
                <Typography variant={"body2"} style={{ fontWeight: "inherit" }}>
                  {ActualDateTime(item.createdAt)}
                </Typography>
              </Stack>
            </MenuItem>
          ))}
          {!notifications.length && (
            <MenuItem disabled={true}>
              <ListItemText sx={{ textAlign: "center" }}>
                <Typography variant={"caption"}>
                  {t("noNotifications.msg")}
                </Typography>
              </ListItemText>
            </MenuItem>
          )}
        </MenuList>
        <Divider/>
        <Stack m={1} direction={"row"} justifyContent={"flex-end"}>
          <Button onClick={handleMarkAllAsRead} disabled={!unreadCount}>{t("buttons.markAsRead")}</Button>
          <Button onClick={handleAll}>{t("buttons.seeAll")}</Button>
        </Stack>
      </BasicPopper>
    </Stack>
  );
};

export default NotificationIcon;
